import api from 'api';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { BulkUploadDetailsSchemaContract } from 'schemas/BulkUploadDetailsSchema';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { AppError } from 'utils/AppError';
import { ErrorCard } from 'components/BulkUploads/BladeComponents/ErrorCard';
import { GET_BULK_UPLOAD_STAGING_RECORD } from '../queries/queries';
import {
  Table,
  TableHeaderRow,
  TableHeaderCell,
  TablePagination,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  Box,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { EmptyTableCard } from './EmptyTableCard';

export type arrearsBreakdownType = {
  name?: string | null;
  amount?: number | null;
};

export type PreviewTableProps = {
  bulkUploadRecord: BulkUploadDetailsSchemaContract[0] | null;
  columns: Array<{
    title: string;
    render: (
      item: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0],
    ) => React.ReactNode;
  }>;
  tableTopView?: (
    bulkUploadRecord?: BulkUploadDetailsSchemaContract[0] | null,
    record?: BulkUploadStagingDetailsSchemaContract,
  ) => React.ReactNode;
  tableBottomView?: (
    bulkUploadRecord?: BulkUploadDetailsSchemaContract[0] | null,
    record?: BulkUploadStagingDetailsSchemaContract,
  ) => React.ReactNode;
  sheetNumber?: number;
  activeTabValue?: string;
  onStagingDataLoaded?: (data: BulkUploadStagingDetailsSchemaContract[1]) => void;
};

export const PreviewTable = ({
  bulkUploadRecord,
  columns,
  tableBottomView,
  tableTopView,
  sheetNumber,
  activeTabValue,
  onStagingDataLoaded,
}: PreviewTableProps) => {
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const bulkUploadRecordId = bulkUploadRecord?.id ?? 0;

  const {
    isLoading,
    isRefetching,
    error,
    data: bulkUploadStagingRecord,
  } = useQuery(
    [GET_BULK_UPLOAD_STAGING_RECORD, itemsPerPage, currentPage, bulkUploadRecordId, sheetNumber],
    ({ queryKey }) => {
      const rowLimit = queryKey[1] as number;
      const page = (queryKey[2] as number) + 1;
      const bulkUploadRecordId = queryKey[3] as number;
      const sheetNumber = queryKey[4] as number;
      if (!bulkUploadRecord) {
        return Promise.reject('Bulk upload record not found');
      }

      return api.bulkUploads.fetchBulkUploadStagingRecord(
        bulkUploadRecordId,
        page,
        rowLimit,
        sheetNumber,
      );
    },
    {
      onError: (error: typeof AppError) => {},
      onSuccess: (data: BulkUploadStagingDetailsSchemaContract) => {
        if (data && onStagingDataLoaded) {
          onStagingDataLoaded(data?.bulk_uploads_staging);
        }
      },
    },
  );

  const totalRows = bulkUploadStagingRecord?.bulk_uploads_staging?.total ?? 0;
  const startingRow = currentPage * itemsPerPage + 1;
  const endingRow = Math.min((currentPage + 1) * itemsPerPage, totalRows);

  if (error) {
    return (
      <Box marginTop="spacing.6">
        <ErrorCard error={error} />
      </Box>
    );
  }

  const tableData = bulkUploadStagingRecord?.bulk_uploads_staging?.data ?? [];
  const columnHeaders = columns.map((column) => column.title);
  const columnValueRenderers = columns.map((column) => column.render);

  return (
    <>
      {tableTopView ? tableTopView(bulkUploadRecord, bulkUploadStagingRecord) : null}

      <Box marginTop="spacing.6" maxWidth="calc(100vw - 64px)" overflow="auto">
        {tableData.length > 0 ? (
          <Table
            data={{ nodes: tableData }}
            isLoading={isLoading}
            isRefreshing={isRefetching}
            isFirstColumnSticky
            rowDensity="comfortable"
            showStripedRows
            pagination={
              <TablePagination
                showLabel
                label={`Showing ${startingRow}-${endingRow} of ${totalRows} Rows`}
                currentPage={currentPage}
                paginationType="server"
                showPageSizePicker
                onPageChange={({ page }) => {
                  setCurrentPage(page);
                }}
                onPageSizeChange={({ pageSize }) => {
                  setItemsPerPage(pageSize);
                  setCurrentPage(0);
                }}
                totalItemCount={totalRows}
              />
            }>
            {(nodes) => (
              <>
                <TableHeader>
                  <TableHeaderRow>
                    {columnHeaders.map((title) => (
                      <TableHeaderCell key={title}>{title}</TableHeaderCell>
                    ))}
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {nodes.map((item, idx) => (
                    <TableRow key={item.id} item={item}>
                      {columnValueRenderers.map((render, idx) => (
                        <TableCell key={`${item.id}-${idx}`}>{render?.(item) ?? ''}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </>
            )}
          </Table>
        ) : (
          <EmptyTableCard itemLabel={activeTabValue} />
        )}
      </Box>

      {tableBottomView ? tableBottomView(bulkUploadRecord, bulkUploadStagingRecord) : null}
    </>
  );
};
